import { Button, Label, Select, ToggleSwitch } from 'flowbite-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Periode, SignupPrefs, } from '../lib/model/kantoordagApi';
import { useGetBuildingsQuery } from '../lib/model/kantoordag/buildingsStreamingApi.ts';
import { ROOM__AUTOMATIC_ASSIGNMENT, ROOM__DEFAULT_ROOMS, ROOM__NO_ROOM } from '../app/Constants';
import { QueryResult } from "./Alerts.tsx";
import { FaWindowClose } from "react-icons/fa";

interface PreferenceEditorSlideInProps {
    isOpen: boolean;
    onClose: () => void;
    initialPreferences: SignupPrefs | undefined; // New prop for initial preferences
    onSave: (preferences: SignupPrefs) => void; // New prop for saving preferences locally
}

export default function PreferenceEditorSlideIn({ isOpen, onClose, initialPreferences, onSave }: PreferenceEditorSlideInProps) {
    const { t } = useTranslation("preferences");
    const gebouwQuery = useGetBuildingsQuery()

    const [building, setBuilding] = useState<string | undefined>(initialPreferences?.building ?? undefined);
    const [room, setRoom] = useState<string | undefined>(initialPreferences?.room ?? undefined);
    const [parkingRequested, setParkingRequested] = useState<boolean | undefined>(initialPreferences?.parkingRequested ?? undefined);
    const [periode, setPeriode] = useState<Periode | undefined>(initialPreferences?.periode ?? undefined);

    const handleSave = () => {
        const newSignupPrefs: SignupPrefs = {
            building: building,
            room: room,
            parkingRequested: parkingRequested,
            periode: periode,
        };
        onSave(newSignupPrefs); // Call the onSave callback with the new preferences
        onClose();
    };

    const rooms = useCallback(() => {
        if (building == undefined || gebouwQuery.data == undefined) {
            return ROOM__DEFAULT_ROOMS
        } else {
            const selectedBuilding = gebouwQuery.data.filter(b => b.name == building)[0];
            return selectedBuilding ? selectedBuilding.rooms.map(r => r.name) : ROOM__DEFAULT_ROOMS;
        }
    }, [gebouwQuery.data, building])

    function roomName(roomName: string): string {
        return roomName == ROOM__NO_ROOM || roomName == ROOM__AUTOMATIC_ASSIGNMENT ? t(roomName) : roomName;
    }

    const slideInBaseClasses = `fixed bg-white shadow-lg z-50 p-4 overflow-y-auto transition-transform duration-300 ease-in-out transform`;
    const mobileClasses = `inset-x-0 bottom-0 h-1/2 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`;
    const desktopClasses = `md:translate-y-0 md:inset-y-0 md:right-0 md:w-80 md:h-full md:left-auto ${isOpen ? 'md:translate-x-0' : 'md:translate-x-full'}`;

    return (
        <div
            className={`${slideInBaseClasses} ${mobileClasses} ${desktopClasses}`} data-tut="preferences_edit">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">{t("edit_preferences")}</h3>
                <FaWindowClose color="red" onClick={onClose}>X</FaWindowClose>
            </div>
            <div className="text-sm text-gray-500 mb-4">
                {t('temporary_preferences')}
            </div>

            <QueryResult query={gebouwQuery} message={t("loading_failed")}>
                {
                    gebouwQuery.data ?
                        (
                            <div className="space-y-4">
                                {/* Building selection */}
                                <div className="w-full flex-col">
                                    <Label htmlFor="building">{t('building')}</Label>
                                    <Select id="building"
                                            value={building}
                                            onChange={(e) => setBuilding(e.target.value)}>
                                        <option value="">{t('no_preference')}</option>
                                        {gebouwQuery.data.map((building) => (
                                            <option key={building.name} value={building.name}>{building.name}</option>
                                        ))}
                                    </Select>
                                </div>

                                {/* Room selection */}
                                <div className="w-full flex-col">
                                    <Label htmlFor="room">{t('room')}</Label>
                                    <Select id="room" value={room || ''}
                                            onChange={(e) => setRoom(e.target.value)}>
                                        <option value="">{t('no_preference')}</option>
                                        {rooms().map((room) => (
                                            <option key={room} value={room}>{roomName(room)}</option>
                                        ))}
                                    </Select>
                                </div>

                                {/* Parking requested */}
                                <div className="flex flex-col items-start gap-4">
                                    <ToggleSwitch
                                        checked={parkingRequested ?? false}
                                        onChange={(checked) => setParkingRequested(checked)}
                                        label={t('requestParking')}
                                    />
                                </div>

                                {/* Period */}
                                <div className="w-full flex-col">
                                    <Label htmlFor="periode">{t('periode')}</Label>
                                    <Select id="periode" value={periode || ''} onChange={(e) => setPeriode(e.target.value as Periode)}>
                                        <option value="">{t('no_preference')}</option>
                                        <option value={Periode.HeleDag}>{t('wholeDay')}</option>
                                        <option value={Periode.DagDeel}>{t('partDay')}</option>
                                    </Select>
                                </div>

                                <Button onClick={handleSave} color="primary" className="w-full mt-4">{t("save_preferences")}</Button>
                            </div>
                        ) : null
                }
            </QueryResult>
        </div>
    );
}